import { collection, getDocs, orderBy, query, writeBatch, doc, limit } from 'firebase/firestore';

import { HudlLogo } from '@labradorsports/assets';
import {
    createException,
    Exception,
    findAddressComponents,
    GeneralErrors,
    getPlaceAPI,
    getVimeoVideoID,
    getYouTubeVideoID,
    isHudlUrl,
    safeToDate,
} from '@labradorsports/utils';

import firestore from '../../firebase-config.js';
import { DataTags } from '../../shared/constants.js';
import { getAssetUrl } from '../../shared/utils.js';
import { createApiSlice } from '../api.js';
import { CustomMutationDefinition, CustomQueryDefinition } from '../types.js';

interface PlaceResult {
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    place: {
        id: string;
        lat: number;
        lng: number;
    };
    name: string;
}

export const mainApi = createApiSlice({
    getVideoDetails: {
        type: 'query',
        extraOptions: {
            retryable: true,
        },
        isValid: ({ videoLink }) => Boolean(videoLink),
        queryFn: async ({ videoLink }, { extra }) => {
            const { cff } = extra;
            const hudlUrl = isHudlUrl(videoLink);
            const youtubeUrl = Boolean(getYouTubeVideoID(videoLink));
            const vimeoUrl = Boolean(getVimeoVideoID(videoLink));

            if (videoLink && hudlUrl) {
                return {
                    data: {
                        thumbnailUrl: getAssetUrl(HudlLogo),
                        title: 'Hudl Video',
                        description: '',
                    },
                };
            }

            if (youtubeUrl || vimeoUrl) {
                const videoDetails = await cff.fetch('main/getVideoDetails', { videoLink }, null, true);

                return {
                    data: videoDetails,
                };
            }

            return {
                data: null,
            };
        },
        providesTags: (result: any[], error, { videoLink }) => [{ type: DataTags.VIDEO_DETAILS, id: videoLink }],
    } as CustomQueryDefinition<{ videoLink: string }, any>,

    getVersion: {
        type: 'query',
        query: () => ({
            path: 'main/version',
        }),
        transformResponse: ({ version, deployed }) => {
            return [version, new Date(deployed)];
        },
    } as CustomQueryDefinition<void, any>,

    getGlobalSearch: {
        type: 'query',
        extraOptions: {
            unloggableArg: ['query'],
        },
        isValid: ({ query, playbookId }) => Boolean(query && playbookId),
        query: ({ query, playbookId, teamIds }) => ({
            path: 'search/global',
            query: {
                query,
                playbookId,
                teamIds: teamIds.join(','),
            },
        }),
        onQueryEnded: ({ data }, { extra }) => {
            const { logger } = extra;
            const { posts, events, roster, plays } = data;

            logger.log('getGlobalSearch complete', {
                posts: posts.length,
                events: events.length,
                roster: roster.length,
                plays: plays.length,
            });
        },
    } as CustomQueryDefinition<
        { query: string; playbookId: string; teamIds: string[] },
        { posts: []; events: []; roster: []; plays: [] }
    >,

    searchPlaces: {
        type: 'query',
        extraOptions: {
            unloggableArg: ['query'],
        },
        isValid: ({ query }) => Boolean(query),
        queryFn: async ({ query }) => {
            const request = {
                textQuery: query,
                fields: ['formattedAddress', 'addressComponents', 'location'],
                language: 'en-US',
                maxResultCount: 5,
                region: 'us',
            };

            const placeAPI = await getPlaceAPI();

            try {
                const { places } = await placeAPI.searchByText(request);

                return {
                    data: places.map((place) => ({
                        ...findAddressComponents(place),
                        place: {
                            id: place.id,
                            lat: place.location.lat(),
                            lng: place.location.lng(),
                        },
                        name: place.formattedAddress,
                    })),
                };
            } catch (err) {
                throw createException(GeneralErrors.GOOGLE_MAPS_ERROR, {
                    nestedError: err,
                    details: 'Error searching for places.',
                });
            }
        },
    } as CustomQueryDefinition<{ query: string }, PlaceResult[]>,

    sendDeveloperNote: {
        type: 'mutation',
        query: ({ note, error }) => ({
            path: 'main/sendDeveloperNote',
            body: {
                note,
                error: typeof error.toJSON === 'function' ? error.toJSON() : error,
                build: BUILD,
                client: APP ? 'native' : 'web',
            },
        }),
    } as CustomMutationDefinition<{ note: string; error: Exception }, void>,

    forceAPIHandlerError: {
        type: 'mutation',
        queryFn: () => {
            throw new Error('Test API handler error');
        },
    } as CustomMutationDefinition<void, void>,

    forceAPIError: {
        type: 'mutation',
        query: () => ({
            path: 'main/forceError',
        }),
    } as CustomMutationDefinition<void, void>,

    getNotifications: {
        type: 'query',
        queryFn: async ({ uid }) => {
            const notifications = await getDocs(
                query(collection(firestore, 'users', uid, 'notifications'), orderBy('createdAt', 'desc'), limit(100))
            );

            return {
                data: notifications.docs.map((doc) => {
                    const data = doc.data();
                    return {
                        ...data,
                        id: doc.id,
                        createdAt: safeToDate(data.createdAt),
                    };
                }),
            };
        },
        providesTags: (result, meta, { uid }) => {
            const baseTags = [{ type: DataTags.NOTIFICATION, id: `${uid}_LIST` }];

            if (!result) {
                return baseTags;
            }

            const notificationTags = result.map((notification) => ({
                type: DataTags.NOTIFICATION,
                id: `${uid}_${notification.id}`,
            }));

            return [...baseTags, ...notificationTags];
        },
    } as CustomQueryDefinition<{ uid: string }, any[]>,

    markNotificationsRead: {
        type: 'mutation',
        isValid: ({ uid, notificationIds }) =>
            Boolean(uid && notificationIds && Array.isArray(notificationIds) && notificationIds.length > 0),
        queryFn: async ({ uid, notificationIds }) => {
            const batch = writeBatch(firestore);

            // Update each notification to mark as read
            notificationIds.forEach((notificationId: string) => {
                const notificationRef = doc(firestore, 'users', uid, 'notifications', notificationId);
                batch.update(notificationRef, { read: true });
            });

            await batch.commit();
        },
        invalidatesTags: (result, meta, { uid, notificationIds }) => [
            { type: DataTags.NOTIFICATION, id: `${uid}_LIST` },
            ...notificationIds.map((id) => ({ type: DataTags.NOTIFICATION, id: `${uid}_${id}` })),
        ],
    } as CustomMutationDefinition<{ uid: string; notificationIds: string[] }, void>,

    getGlobalSuppression: {
        type: 'query',
        extraOptions: {
            unloggableArg: ['email'],
            anonymousHooks: true,
        },
        isValid: ({ email, uid }) => Boolean(email || uid),
        query: ({ email }) => ({
            path: 'main/getGlobalSuppression',
            query: { email },
        }),
        providesTags: (result, error, { uid }) => {
            if (uid) {
                return [{ type: DataTags.GLOBAL_SUPPRESSION, id: uid }];
            }
            return [];
        },
    } as CustomQueryDefinition<{ email?: string; uid?: string }, { isSuppressed: boolean }>,

    addGlobalSuppression: {
        type: 'mutation',
        query: () => ({
            path: 'main/addGlobalSuppression',
            body: {},
        }),
        invalidatesTags: (result, error, { uid }) => {
            if (uid) {
                return [{ type: DataTags.GLOBAL_SUPPRESSION, id: uid }];
            }
            return [];
        },
    } as CustomMutationDefinition<{ uid: string }, { success: boolean }>,

    deleteGlobalSuppression: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['email'],
            anonymousHooks: true,
        },
        isValid: ({ uid, email }) => Boolean(uid || email),
        query: ({ email }) => ({
            path: 'main/deleteGlobalSuppression',
            body: {
                email,
            },
        }),
        invalidatesTags: (result, error, { uid }) => {
            if (uid) {
                return [{ type: DataTags.GLOBAL_SUPPRESSION, id: uid }];
            }
            return [];
        },
    } as CustomMutationDefinition<{ uid?: string; email?: string }, { success: boolean }>,
});

export const {
    useGetVideoDetailsQuery,
    useGetVersionQuery,
    useGetGlobalSearchQuery,
    useSearchPlacesQuery,
    useSendDeveloperNoteMutation,
    useForceAPIHandlerErrorMutation,
    useForceAPIErrorMutation,
    useGetNotificationsQuery,
    useMarkNotificationsReadMutation,
    useGetGlobalSuppressionQuery,
    useAddGlobalSuppressionMutation,
    useDeleteGlobalSuppressionMutation,
} = mainApi;
